import React, { useMemo } from 'react';
import { Button, Card, Empty, Table, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from './ColumnConfigDrawer';

const FIXED_KEYS = ['name'];

/**
 * "Visits by client" table — unified view that normalizes pickup vs delivery
 * names (strips the partner code prefix, e.g. "STER, Hôpital Jules-Gonin"
 * collapses to "Hôpital Jules-Gonin" to match the bare pickup name) and
 * deduplicates passages PER DAY. If a client is touched on both sides on
 * the same date, it counts as one visit-day.
 *
 * Columns:
 *   - Client name (normalized)
 *   - Total visit days
 *   - Pickup days (days with at least one pickup at this client)
 *   - Delivery days (days with at least one delivery at this client)
 *   - Completion % across all touching orders (quality signal)
 */
export default function StatsByName({ data, loading, titleSuffix }) {
  const rows = data?.rows || [];

  const columns = useMemo(
    () => [
      {
        title: 'Client',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
      },
      {
        title: (
          <Tooltip title="Number of distinct days OVO visited this client (counted once per day even if both pickup and delivery happened)">
            Total visits
          </Tooltip>
        ),
        configLabel: 'Total visits',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        width: 130,
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.total - b.total,
      },
      {
        title: <Tooltip title="Days with at least one pickup at this client">Pickup days</Tooltip>,
        configLabel: 'Pickup days',
        dataIndex: 'pickupDays',
        key: 'pickupDays',
        align: 'right',
        width: 120,
        sorter: (a, b) => a.pickupDays - b.pickupDays,
      },
      {
        title: <Tooltip title="Days with at least one delivery at this client">Delivery days</Tooltip>,
        configLabel: 'Delivery days',
        dataIndex: 'deliveryDays',
        key: 'deliveryDays',
        align: 'right',
        width: 130,
        sorter: (a, b) => a.deliveryDays - b.deliveryDays,
      },
      {
        title: <Tooltip title="Across all orders touching this client (pickup + delivery)">Orders</Tooltip>,
        configLabel: 'Orders',
        dataIndex: 'orders',
        key: 'orders',
        align: 'right',
        width: 100,
        sorter: (a, b) => a.orders - b.orders,
      },
      {
        title: 'Completion %',
        dataIndex: 'completionRate',
        key: 'completionRate',
        align: 'right',
        width: 130,
        render: (v) => `${v?.toFixed?.(1) ?? v} %`,
        sorter: (a, b) => a.completionRate - b.completionRate,
      },
    ],
    [],
  );

  // Page size lives under the gear (house rule) — no bottom-right size
  // changer. Client-side pagination: the gear's persisted pageSize drives it
  // directly; a single page hides the pagination bar entirely.
  const columnConfig = useColumnConfig('stats-by-name-columns', columns, FIXED_KEYS);
  const paginationConfig = useMemo(() => {
    if (rows.length <= columnConfig.pageSize) return false;
    return {
      pageSize: columnConfig.pageSize,
      showSizeChanger: false,
      showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
      size: 'small',
    };
  }, [rows.length, columnConfig.pageSize]);

  const title = titleSuffix ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span>Visits by client</span>
      <span className="section-range">{titleSuffix}</span>
    </div>
  ) : (
    'Visits by client'
  );

  return (
    <Card
      title={title}
      extra={<Button type="text" size="small" icon={<SettingOutlined />} onClick={columnConfig.openDrawer} />}
    >
      {rows.length === 0 && !loading ? (
        <Empty description="No data for this period" />
      ) : (
        <Table
          // Guarantee uniqueness even if two clients somehow normalize to the
          // same name (backend dedup is server-side string match — defensive
          // fallback so React keys never collide and pagination doesn't glitch).
          rowKey={(record, index) => `${record.name || '∅'}__${index}`}
          size="small"
          loading={loading}
          dataSource={rows}
          columns={columnConfig.visibleColumns}
          pagination={paginationConfig}
        />
      )}
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={columnConfig.setPageSize}
      />
    </Card>
  );
}
